import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Play, RefreshCw, FileText, Database, Flag, ScrollText,
  Gavel, History, Upload, Loader2, X, CheckCircle, CheckCircle2, AlertCircle,
  ExternalLink, Printer, Ban, Download, MessageSquare, Trash2, Sparkles,
} from 'lucide-react';
import { useAuthStore } from '../stores/auth';
import { useToastStore } from '../stores/toast';
import apiClient from '../api/client';
import {
  getCase, updateCase, reReviewCase, listDocuments, getDocument,
  type CaseDetail as CaseDetailType, type CaseFact, type DocumentItem,
} from '../api/cases';
import {
  listReviewRuns, runFullReview, getReviewRun, cancelReview, overrideFlag, getDetermination,
  recordDetermination, listPublicData, triggerPublicLookup, getPublicDataRun, getLatestPublicDataRun, getPublicDataHistory, listAuditEntries,
  type ReviewRun, type ReviewRunDetail, type Determination,
  type PublicDataResult, type AuditEntry,
} from '../api/review';
import {
  openReviewRecordHtml,
  downloadReviewRecordJson,
} from '../api/reviewRecord';
import TierBadge from '../components/TierBadge';
import StatusBadge from '../components/StatusBadge';
import FlagCard from '../components/FlagCard';
import DocumentRow from '../components/DocumentRow';
import WorkflowPanel from '../components/WorkflowPanel';
import ChatPanel from '../components/ChatPanel';
import SavedResponsesPanel from '../components/SavedResponsesPanel';
import RulesOverridePanel from '../components/case/RulesOverridePanel';
import { CreateRulePanel } from '../components/learned-rules/CreateRulePanel';
import { purgeCaseDocuments } from '../api/documents';
import { getCaseWorkflow, type CaseWorkflow } from '../api/workflow';
import {
  submitFlagFeedback,
  createMissedAbnormality,
  listMissedAbnormalities,
  type FlagFeedback,
  type FlagFeedbackDisposition,
  type MissedAbnormality,
} from '../api/feedback';


type Tab = 'overview' | 'documents' | 'public-data' | 'flags' | 'summary' | 'determination' | 'audit' | 'chat';

// Simple markdown-to-HTML renderer (no external deps)
function renderMarkdown(md: string): string {
  let html = md
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  // Headers
  html = html.replace(/^#{6}\s+(.+)$/gm, '<h6 class="text-sm font-semibold text-slate-700 mt-3 mb-1">$1</h6>');
  html = html.replace(/^#{5}\s+(.+)$/gm, '<h5 class="text-sm font-semibold text-slate-700 mt-3 mb-1">$1</h5>');
  html = html.replace(/^#{4}\s+(.+)$/gm, '<h4 class="text-sm font-semibold text-slate-700 mt-3 mb-1">$1</h4>');
  html = html.replace(/^#{3}\s+(.+)$/gm, '<h3 class="text-base font-semibold text-slate-800 mt-4 mb-2">$1</h3>');
  html = html.replace(/^#{2}\s+(.+)$/gm, '<h2 class="text-lg font-bold text-slate-800 mt-4 mb-2">$1</h2>');
  html = html.replace(/^#\s+(.+)$/gm, '<h1 class="text-xl font-bold text-slate-900 mt-4 mb-2">$1</h1>');
  // Bold and italic
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code class="text-xs bg-slate-100 px-1 rounded">$1</code>');
  // Unordered lists
  html = html.replace(/^(\s*)[-\*]\s+(.+)$/gm, '<li class="ml-4 text-sm text-slate-700">$2</li>');
  html = html.replace(/(<li[^>]*>.*<\/li>)/gs, (m) => '<ul class="space-y-1 my-2">' + m + '</ul>');
  // Ordered lists
  html = html.replace(/^\d+\.\s+(.+)$/gm, '<li class="ml-4 text-sm text-slate-700 list-decimal">$1</li>');
  // Horizontal rule
  html = html.replace(/^---+$/gm, '<hr class="border-slate-200 my-3" />');
  // Paragraphs (double newline = paragraph break)
  html = html.replace(/\n\n/g, '</p><p class="text-sm text-slate-700 leading-relaxed mb-2">');
  // Single newlines → <br/>
  html = html.replace(/\n/g, '<br/>');
  // Wrap in paragraph
  html = '<p class="text-sm text-slate-700 leading-relaxed mb-2">' + html + '</p>';
  return html;
}

// Format public data JSONB as human-readable key-value pairs
function formatPublicData(data: Record<string, unknown> | null | undefined): Array<{ key: string; value: unknown; source: string }> {
  if (!data || typeof data !== 'object') return [];
  const items: Array<{ key: string; value: unknown; source: string }> = [];
  // Metadata keys that are already shown in the card header — skip them
  // to avoid duplicating the source URL + timestamp for every data point.
  const SKIP_KEYS = new Set(['source_url', 'retrieved_at', 'screenshot_path', 'pdf_path']);
  for (const [k, v] of Object.entries(data)) {
    if (v === null || v === undefined) continue;
    if (SKIP_KEYS.has(k)) continue;
    if (typeof v === 'object' && 'value' in v) {
      const val = (v as Record<string, unknown>).value;
      const src = (v as Record<string, unknown>).source_url as string || '';
      if (val !== null && val !== undefined) {
        items.push({ key: k, value: val, source: src });
      }
    } else {
      items.push({ key: k, value: v, source: '' });
    }
  }
  return items;
}

// Fields that contain monetary values (format with $ + commas).
const MONETARY_FIELDS = new Set([
  'revenue', 'total_assets', 'goodwill', 'goodwill_intangibles',
  'market_cap', 'consideration_paid', 'share_price', 'assets',
  'last_price', 'previous_close', 'bid_price', 'ask_price',
]);

// Format a number as currency.
function formatMoney(n: number): string {
  if (Math.abs(n) >= 1_000_000_000) return '$' + (n / 1_000_000_000).toFixed(2) + 'B';
  if (Math.abs(n) >= 1_000_000) return '$' + (n / 1_000_000).toFixed(2) + 'M';
  if (Math.abs(n) >= 1_000) return '$' + n.toLocaleString();
  return '$' + String(n);
}

// Format a number with commas (for share counts, volume, etc.).
function formatNumber(n: number): string {
  return n.toLocaleString();
}

// Render any JSON value in a human-readable way.
function renderValue(v: unknown, fieldName?: string): React.ReactNode {
  if (v === null || v === undefined) return <span className="text-slate-300">-</span>;
  if (typeof v === 'string') return renderMaybeUrl(v);
  if (typeof v === 'number') {
    // Format monetary fields with $ and scale; other numbers with commas.
    if (fieldName && MONETARY_FIELDS.has(fieldName.toLowerCase())) {
      return <span>{formatMoney(v)}</span>;
    }
    // Large integers → format with commas (share counts, volume, etc.)
    if (Number.isInteger(v) && Math.abs(v) >= 1000) {
      return <span>{formatNumber(v)}</span>;
    }
    return <span>{String(v)}</span>;
  }
  if (typeof v === 'boolean') return <span>{String(v)}</span>;
  if (Array.isArray(v)) {
    if (v.length === 0) return <span className="text-slate-400">empty</span>;
    // If the array is a list of objects that each carry a 'url' (e.g. EDGAR
    // recent filings), render each as a clickable link instead of a key dump.
    if (v.length > 0 && typeof v[0] === 'object' && v[0] !== null && 'url' in (v[0] as Record<string, unknown>) && 'form' in (v[0] as Record<string, unknown>)) {
      return (
        <div className="space-y-1">
          {v.map((item, i) => {
            const f = item as Record<string, unknown>;
            const url = String(f.url || '');
            const label = `${String(f.form || '')}${f.date ? ' (' + f.date + ')' : ''}`;
            return (
              <div key={i} className="text-xs break-words">
                {url ? (
                  <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                    {label || url}
                  </a>
                ) : (
                  <span className="text-slate-700">{label}</span>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    return (
      <div className="space-y-1">
        {v.map((item, i) => (
          <div key={i} className="text-xs text-slate-700 break-words">
            {renderValue(item, fieldName)}
          </div>
        ))}
      </div>
    );
  }
  if (typeof v === 'object') {
    const obj = v as Record<string, unknown>;
    const keys = Object.keys(obj);
    return (
      <div className="space-y-0.5">
        {keys.map(k => (
          <div key={k} className="flex gap-2 text-xs">
            <span className="font-medium text-slate-500 shrink-0">{prettyKey(k)}:</span>
            <span className="text-slate-700 break-words">{renderValue(obj[k], k)}</span>
          </div>
        ))}
      </div>
    );
  }
  return <span>{String(v)}</span>;
}

// Pretty label for public data keys
// Render a string as a clickable anchor if it looks like an http(s) URL, else plain text.
function renderMaybeUrl(s: string): React.ReactNode {
  if (s && /^https?:\/\//i.test(s)) {
    return (
      <a href={s} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline break-words">
        {s}
      </a>
    );
  }
  return s;
}

function prettyKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const TABS: { id: Tab; label: string; icon: typeof FileText }[] = [
  { id: 'overview', label: 'Overview', icon: FileText },
  { id: 'documents', label: 'Documents', icon: Upload },
  { id: 'public-data', label: 'Public Data', icon: Database },
  { id: 'flags', label: 'Red Flags', icon: Flag },
  { id: 'summary', label: 'Summary', icon: ScrollText },
  { id: 'determination', label: 'Determination', icon: Gavel },
  { id: 'audit', label: 'Audit Trail', icon: History },
  { id: 'chat', label: 'Chat', icon: MessageSquare },
];

const SEVERITY_ORDER: Record<string, number> = {
  AUTO_ESCALATE: 0, HIGH: 1, MEDIUM: 2, LOW: 3,
};

export default function CaseDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const { addToast } = useToastStore();
  const isPrincipal = user?.role === 'principal';
  const isComplianceOrOperator = user?.role === 'compliance' || user?.role === 'operator';

  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [caseData, setCaseData] = useState<CaseDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [reviewRuns, setReviewRuns] = useState<ReviewRun[]>([]);
  const [latestRun, setLatestRun] = useState<ReviewRunDetail | null>(null);
  const [determination, setDetermination] = useState<Determination | null>(null);
  const [publicData, setPublicData] = useState<PublicDataResult[]>([]);
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number; name: string } | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState<{ name: string; size: number; done: boolean }[]>([]);
  const [viewingStoredData, setViewingStoredData] = useState<string | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [viewingDoc, setViewingDoc] = useState<DocumentItem | null>(null);
  const [docText, setDocText] = useState<string | null>(null);
  const [docTextLoading, setDocTextLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editNotes, setEditNotes] = useState('');
  const [editFacts, setEditFacts] = useState({
    issuer_name: '',
    issuer_symbol: '',
    cusip: '',
    depositor_name: '',
    depositor_type: '',
    account_ref: '',
    deposit_date: '',
    share_quantity: '' as string | number,
  });
  const [detDecision, setDetDecision] = useState<Determination['decision'] | ''>('');
  const [detRationale, setDetRationale] = useState('');
  const [detSubmitting, setDetSubmitting] = useState(false);
  const [showDetConfirm, setShowDetConfirm] = useState(false);
  const [refreshingData, setRefreshingData] = useState(false);
  const [pollingPdRunId, setPollingPdRunId] = useState<string | null>(null);
  const [searchHistory, setSearchHistory] = useState<Array<{ timestamp: string; source_count: number; sources: string[]; result_count: number; items: PublicDataResult[] }>>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [refreshingSource, setRefreshingSource] = useState<string | null>(null);
  // Background-review in-progress state.
  const [reviewInProgress, setReviewInProgress] = useState(false);
  const [pollingRunId, setPollingRunId] = useState<string | null>(null);
  // reload public data as soon as the pull completes (data_fetched_at
  // appears on the running review), NOT only when the whole review finishes.
  // The public-data pull typically completes ~40s into a review while the
  // scoring/LLM phase runs minutes longer; without this the Public Data tab
  // stays empty until the review completes or the user manually refreshes.
  const pdReloadedForRunRef = useRef<string | null>(null);
  const [reviewFailedMsg, setReviewFailedMsg] = useState<string | null>(null);
  const [firstReviewEver, setFirstReviewEver] = useState(false);
  const [cancellingReview, setCancellingReview] = useState(false);
  // "Modified - needs re-review" banner shown after a material fact edit.
  const [showRereviewBanner, setShowRereviewBanner] = useState(false);
  const [rereviewLoading, setRereviewLoading] = useState(false);
  // "Train from this case" modal (learned-rule creation, case locked).
  const [showTrainModal, setShowTrainModal] = useState(false);
  // filter the Chat tab out when the user cannot chat.
  const visibleTabs = TABS.filter((t) => t.id !== 'chat' || !!user?.can_chat_case);
  // bump this nonce to refresh the Saved
  // responses panel after a new response is saved from the chat.
  const [savedResponsesNonce, setSavedResponsesNonce] = useState(0);
  // learning-cycle feedback (assigned personnel only).
  const [caseWorkflow, setCaseWorkflow] = useState<CaseWorkflow | null>(null);
  const [flagFeedback, setFlagFeedback] = useState<Record<string, FlagFeedback>>({});
  const [missedAbnormalities, setMissedAbnormalities] = useState<MissedAbnormality[]>([]);
  const [missedNote, setMissedNote] = useState('');
  const [missedSubmitting, setMissedSubmitting] = useState(false);

  // Show all results grouped by source (latest result per query_type
  // per source). A single review run fetches sources sequentially, so
  // they land at slightly different timestamps — filtering by "most
  // recent minute" would drop earlier-fetched sources like EDGAR.
  const groupedPublicData = (() => {
    if (publicData.length === 0) return [];
    // CPDS is an internal code-reuse bridge, not a user-facing source - hide its card.
    const visible = publicData.filter((p) => p.source !== 'cpds');
    if (visible.length === 0) return [];
    // Group by source: merge all facts from the same source into one card.
    // Keep the latest result per query_type within each source.
    const bySource: Record<string, { source: string; items: typeof publicData; has_screenshot: boolean; has_pdf: boolean; source_url: string | null; retrieved_at: string }> = {};
    const seenBySourceKey: Record<string, Set<string>> = {};
    // Sort by retrieved_at desc so we keep the newest per query_type.
    const sorted = [...visible].sort((a, b) => (b.retrieved_at || '').localeCompare(a.retrieved_at || ''));
    for (const p of sorted) {
      if (!bySource[p.source]) {
        bySource[p.source] = { source: p.source, items: [], has_screenshot: false, has_pdf: false, source_url: null, retrieved_at: p.retrieved_at };
        seenBySourceKey[p.source] = new Set();
      }
      const key = `${p.source}:${p.query_type}`;
      if (seenBySourceKey[p.source].has(key)) continue; // already have a newer one
      seenBySourceKey[p.source].add(key);
      bySource[p.source].items.push(p);
      if (p.has_screenshot) bySource[p.source].has_screenshot = true;
      if (p.has_pdf) bySource[p.source].has_pdf = true;
      if (p.source_url && !bySource[p.source].source_url) bySource[p.source].source_url = p.source_url;
    }
    return Object.values(bySource).sort((a, b) => a.source.localeCompare(b.source));
  })();

  const loadCase = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      // Fetch case, documents, review runs, determination, audit in parallel.
      // Public data is fetched AFTER runs so we can filter by the latest
      // completed review run's id.
      const [cd, docs, runs, det, audit] = await Promise.all([
        getCase(id),
        listDocuments(id),
        listReviewRuns(id),
        getDetermination(id),
        listAuditEntries(id),
      ]);
      setCaseData(cd);
      setDocuments(docs.items || []);
      setReviewRuns(runs.items || []);
      setDetermination(det);
      setAuditEntries(audit.items || []);
      resetEditFromCase(cd.case);

      // load case workflow (for reviewer-assignment RBAC) +
      // missed-abnormality notes. Both non-fatal.
      try {
        const wf = await getCaseWorkflow(id);
        setCaseWorkflow(wf);
      } catch { setCaseWorkflow(null); }
      try {
        const ma = await listMissedAbnormalities(id);
        setMissedAbnormalities(ma.items || []);
      } catch { setMissedAbnormalities([]); }

      // Determine the latest COMPLETED run to filter public data by.
      // The API returns runs newest-first; pick the highest run_number
      // (not .pop(), which on a newest-first list would select the OLDEST).
      const runItems = runs.items || [];
      const latestCompleted = runItems
        .filter((r) => r.status === 'COMPLETED')
        .sort((a, b) => (b.run_number ?? 0) - (a.run_number ?? 0))[0] || undefined;
      const runningRun = runItems.find((r) => r.status === 'RUNNING') || null;

      // Fetch ALL public data (no run_id filter). Reviews no longer
      // auto-fetch data — the data's review_run_id points to the run
      // that FETCHED it (or null for manual Refresh Data), not the run
      // that USED it. The groupedPublicData dedup keeps only the latest
      // value per (source, query_type) from the most recent fetch batch.
      const pd = await listPublicData(id);
      // Keep ALL rows; the groupedPublicData memo dedups by (source,
      // query_type) keeping the newest retrieved_at, so each source card
      // shows one value per query_type (e.g. edgar -> revenue, assets,
      // goodwill, sic_code, ...). Do NOT dedup by source here - that would
      // collapse each card to a single item.
      setPublicData(pd.items || []);
      try {
        const hist = await getPublicDataHistory(id);
        setSearchHistory(hist.history || []);
      } catch { setSearchHistory([]); }

      // Detect an in-flight public-data refresh (e.g. the user navigated
      // away + back while a refresh was running) so the Refresh Data button
      // stays disabled + we resume polling it instead of looking idle.
      try {
        const pdRun = await getLatestPublicDataRun(id);
        if (pdRun.status === 'RUNNING' && pdRun.run_id) {
          setRefreshingData(true);
          setPollingPdRunId(pdRun.run_id);
        } else {
          setRefreshingData(false);
          setPollingPdRunId(null);
        }
      } catch { /* non-fatal */ }

      // show the latest COMPLETED run (never blank during a re-run).
      // If a review is RUNNING, show the in-progress banner over the last
      // completed results and poll until COMPLETED/FAILED.
      if (latestCompleted) {
        const detail = await getReviewRun(id, latestCompleted.id);
        setLatestRun(detail);
      } else {
        setLatestRun(null);
      }
      if (runningRun) {
        setReviewInProgress(true);
        setPollingRunId(runningRun.id);
        setReviewFailedMsg(null);
        if (!latestCompleted) setFirstReviewEver(true);
      } else {
        setReviewInProgress(false);
        setPollingRunId(null);
        setFirstReviewEver(false);
        // Surface a FAILED latest run (if any) so the user can re-run.
        // API returns runs newest-first; the latest is the first element.
        const latestOverall = runItems[0];
        if (latestOverall && latestOverall.status === 'FAILED') {
          setReviewFailedMsg(latestOverall.error_message || 'Review failed');
        } else {
          setReviewFailedMsg(null);
        }
      }
    } catch {
      addToast('Failed to load case', 'error');
    } finally {
      setLoading(false);
    }
  }, [id, addToast]);

  useEffect(() => { loadCase(); }, [loadCase]);

  // poll a background review run every ~4s until COMPLETED/FAILED.
  useEffect(() => {
    if (!id || !pollingRunId) return;
    // Reset the public-data-reload guard when a new run starts polling.
    pdReloadedForRunRef.current = null;
    let cancelled = false;
    const poll = async () => {
      try {
        const detail = await getReviewRun(id, pollingRunId);
        if (cancelled) return;
        const st = detail.run.status;
        // the public-data pull finishes early in the run (data_fetched_at
        // becomes non-null) while the scoring/LLM phase keeps the review RUNNING
        // for minutes. Reload public data once as soon as the pull completes so
        // the Public Data tab populates without a manual refresh.
        if (st === 'RUNNING'
          && detail.run.data_fetched_at
          && pdReloadedForRunRef.current !== pollingRunId) {
          pdReloadedForRunRef.current = pollingRunId;
          try {
            const pd = await listPublicData(id);
            if (!cancelled) setPublicData(pd.items || []);
            const hist = await getPublicDataHistory(id);
            if (!cancelled) setSearchHistory(hist.history || []);
          } catch { /* non-fatal; the COMPLETED reload will retry */ }
        }
        if (st === 'COMPLETED') {
          setLatestRun(detail);
          setReviewInProgress(false);
          setPollingRunId(null);
          setReviewFailedMsg(null);
          setFirstReviewEver(false);
          addToast('Review completed', 'success');
          // Reload case + public data (pipeline writes public_data_results + tier).
          loadCase();
        } else if (st === 'FAILED') {
          setReviewInProgress(false);
          setPollingRunId(null);
          setReviewFailedMsg(detail.run.error_message || 'Review failed');
          setFirstReviewEver(false);
          addToast('Review failed: ' + (detail.run.error_message || 'unknown error'), 'error');
        } else if (st === 'CANCELLED') {
          setReviewInProgress(false);
          setPollingRunId(null);
          setReviewFailedMsg(null);
          setFirstReviewEver(false);
          addToast('Review cancelled', 'info');
          loadCase();
        }
      } catch {
        // network blip - keep polling
      }
    };
    poll(); // immediate first poll
    const handle = setInterval(poll, 4000);
    return () => { cancelled = true; clearInterval(handle); };
  }, [pollingRunId, id, addToast, loadCase]);

  // Poll an in-flight public-data refresh run (set on mount if a refresh was
  // already running, or set by handleRefreshData/handleRefreshSource). Cancels
  // on unmount so navigating away does not leave a dangling loop.
  useEffect(() => {
    if (!id || !pollingPdRunId) return;
    let cancelled = false;
    let done = false; // guard so the interval does not re-fire the handler
    const poll = async () => {
      if (done || cancelled) return;
      // Status poll: a network blip here just means we retry next tick.
      let run;
      try {
        run = await getPublicDataRun(id, pollingPdRunId);
      } catch { return; }
      if (done || cancelled) return;
      if (run.status === 'COMPLETED') {
        done = true;
        setPollingPdRunId(null);
        setRefreshingData(false);
        setRefreshingSource(null);
        // Reload the whole case (public data + history + case state) - the
        // canonical screen refresh, same path as a manual reload. Errors
        // here are real (not blips) so surface them rather than swallowing.
        try {
          await loadCase();
          addToast('Public data refreshed', 'success');
        } catch {
          addToast('Public data refresh completed - reload the case to see it', 'success');
        }
      } else if (run.status === 'FAILED') {
        done = true;
        setPollingPdRunId(null);
        setRefreshingData(false);
        setRefreshingSource(null);
        addToast('Public data refresh failed' + (run.error_message ? ': ' + run.error_message : ''), 'error');
      }
    };
    poll();
    const handle = setInterval(poll, 4000);
    return () => { cancelled = true; clearInterval(handle); };
  }, [pollingPdRunId, id, addToast, loadCase]);

  const handleRunReview = async () => {
    if (!id) return;
    setReviewLoading(true);
    setReviewFailedMsg(null);
    try {
      const resp = await runFullReview(id);
      // 202 + {run_id, status:'RUNNING', case_id} -
      setReviewInProgress(true);
      setPollingRunId(resp.run_id);
      setFirstReviewEver(!latestRun);
      addToast('Review started', 'success');
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      if (e?.response?.status === 409) {
        addToast('A review is already in progress', 'error');
        setReviewInProgress(true);
      } else {
        addToast('Failed to start review', 'error');
      }
    } finally {
      setReviewLoading(false);
    }
  };

  const handleCancelReview = async () => {
    if (!id || !pollingRunId) return;
    setCancellingReview(true);
    try {
      await cancelReview(id, pollingRunId);
      // The poll loop will pick up status='CANCELLED' and clear in-progress.
      addToast('Review cancelled', 'info');
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      if (e?.response?.status === 409) {
        addToast('Review is no longer running', 'info');
        // Force a re-evaluation: stop polling + reload.
        setReviewInProgress(false);
        setPollingRunId(null);
        loadCase();
      } else {
        addToast('Failed to cancel review', 'error');
      }
    } finally {
      setCancellingReview(false);
    }
  };

  const handleOverrideFlag = async (flagId: string, reason: string) => {
    try {
      await overrideFlag(flagId, reason);
      addToast('Flag marked as false positive', 'success');
      // Refresh the review run so the flag's override status (and the
      // recalculation that excludes overridden flags) is reflected.
      loadCase();
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      const msg = e?.response?.data?.detail || 'Failed to override flag';
      addToast(msg, 'error');
      throw err;
    }
  };

  // whether the current user may submit learning-cycle feedback
  // on this case (assigned personnel = oversight, assigned analyst, case
  // creator, or a reviewer assigned to a workflow step).
  const canFeedback = (() => {
    if (!user || !caseData) return false;
    if (user.role === 'principal' || user.role === 'compliance' || user.role === 'operator') return true;
    if (user.can_see_all_cases) return true;
    if (caseData.case.assigned_analyst_id === user.id) return true;
    if (caseData.case.created_by === user.id) return true;
    if (caseWorkflow?.steps?.some((s) => s.assigned_reviewer_id === user.id)) return true;
    return false;
  })();

  const handleFlagFeedback = async (
    flagId: string,
    disposition: FlagFeedbackDisposition,
    note?: string,
  ) => {
    if (!id) return;
    try {
      const fb = await submitFlagFeedback(id, flagId, disposition, note);
      setFlagFeedback((prev) => ({ ...prev, [flagId]: fb }));
      addToast(`Feedback saved: ${disposition.replace('_', ' ')}`, 'success');
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      const msg = e?.response?.data?.detail || 'Failed to save feedback';
      addToast(msg, 'error');
      throw err;
    }
  };

  const handleAddMissedAbnormality = async () => {
    if (!id || !missedNote.trim()) return;
    setMissedSubmitting(true);
    try {
      const ma = await createMissedAbnormality(id, missedNote.trim());
      setMissedAbnormalities((prev) => [ma, ...prev]);
      setMissedNote('');
      addToast('Missed abnormality recorded', 'success');
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      const msg = e?.response?.data?.detail || 'Failed to record missed abnormality';
      addToast(msg, 'error');
    } finally {
      setMissedSubmitting(false);
    }
  };

  const resetEditFromCase = (caseObj: any) => {
    setEditNotes(caseObj.notes || '');
    setEditFacts({
      issuer_name: caseObj.issuer_name || '',
      issuer_symbol: caseObj.ticker || '',
      cusip: caseObj.cusip || '',
      depositor_name: caseObj.depositor_name || '',
      depositor_type: caseObj.depositor_type || '',
      account_ref: caseObj.account_ref || '',
      deposit_date: caseObj.deposit_date || '',
      share_quantity: caseObj.share_quantity != null ? String(caseObj.share_quantity) : '',
    });
  };

  const handleSaveCase = async () => {
    if (!id) return;
    // Material fact fields editable in the UI (a change to any of these
    // means the prior review/flags may be stale -> offer re-review).
    const MATERIAL_KEYS = [
      'issuer_name', 'issuer_symbol', 'cusip',
      'depositor_name', 'depositor_type', 'account_ref',
      'deposit_date', 'share_quantity',
    ] as const;
    const c = caseData?.case;
    const materialChanged = c
      ? MATERIAL_KEYS.some(
          (k) =>
            String(editFacts[k] ?? '') !==
            String((c as any)[k] ?? '')
        )
      : false;
    try {
      await updateCase(id, {
        notes: editNotes,
        issuer_name: editFacts.issuer_name,
        issuer_symbol: editFacts.issuer_symbol,
        cusip: editFacts.cusip,
        depositor_name: editFacts.depositor_name,
        depositor_type: editFacts.depositor_type,
        account_ref: editFacts.account_ref,
        deposit_date: editFacts.deposit_date
          ? String(editFacts.deposit_date)
          : undefined,
        share_quantity:
          editFacts.share_quantity !== '' &&
          editFacts.share_quantity != null
            ? Number(editFacts.share_quantity)
            : undefined,
      });
      addToast('Case updated', 'success');
      setEditing(false);
      await loadCase();
      if (materialChanged) {
        setShowRereviewBanner(true);
      }
    } catch {
      addToast('Failed to update case', 'error');
    }
  };

  const handleReReview = async () => {
    if (!id) return;
    setRereviewLoading(true);
    try {
      const resp = await reReviewCase(id);
      addToast('Re-pulling public data + re-reviewing...', 'info');
      setShowRereviewBanner(false);
      // Track the launched review run so the existing in-progress polling
      // picks it up and the UI shows the review-running banner.
      setPollingRunId(resp.run_id);
      setReviewInProgress(true);
      setReviewFailedMsg(null);
      await loadCase();
    } catch (e: any) {
      const detail = e?.response?.data?.detail || 'Failed to re-review case';
      addToast(detail, 'error');
    } finally {
      setRereviewLoading(false);
    }
  };

  const handlePurged = useCallback((docId: string) => {
    setDocuments((prev) => prev.map((d) => d.id === docId ? { ...d, is_purged: true } : d));
    addToast('Original purged — file bytes removed from disk.', 'success');
  }, [addToast]);

  const handlePurgeAll = async () => {
    if (!id) return;
    if (!window.confirm(
      'Permanently delete ALL original files for this case from disk?\n' +
      'File bytes are removed; metadata + audit history are retained.\n' +
      'This cannot be undone.'
    )) return;
    try {
      const res = await purgeCaseDocuments(id);
      setDocuments((prev) => prev.map((d) => ({ ...d, is_purged: true })));
      addToast(`Purged ${res.purged_count ?? 0} original file(s).`, 'success');
    } catch (err) {
      console.error('Purge all failed:', err);
      addToast('Failed to purge case documents.', 'error');
    }
  };

  const handleUploadDocuments = async (fileList: FileList | null) => {
    if (!id || !fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    setUploadingDocs(true);
    // Immediately show the files in the uploading list (greyed out in the table)
    const fileEntries = files.map(f => ({ name: f.name, size: f.size, done: false }));
    setUploadingFiles(fileEntries);
    setUploadProgress({ current: 0, total: files.length, name: files[0]?.name || '' });
    addToast(`Uploading ${files.length} document${files.length > 1 ? 's' : ''}...`, 'info');
    try {
      for (let i = 0; i < files.length; i++) {
        setUploadProgress({ current: i, total: files.length, name: files[i].name });
        const formData = new FormData();
        formData.append('file', files[i]);
        await apiClient.post(`/cases/${id}/documents`, formData);
        // Mark this file as done in the uploading list
        setUploadingFiles(prev => prev.map((f, idx) => idx === i ? { ...f, done: true } : f));
      }
      setUploadProgress({ current: files.length, total: files.length, name: '' });
      addToast(`${files.length} document${files.length > 1 ? 's' : ''} uploaded`, 'success');
      loadCase();
    } catch {
      addToast('Upload failed', 'error');
    } finally {
      setUploadingDocs(false);
      setUploadProgress(null);
      setUploadingFiles([]);
    }
  };

  const handleViewDoc = async (doc: DocumentItem) => {
    setViewingDoc(doc);
    setDocTextLoading(true);
    setDocText(null);
    try {
      const detail = await getDocument(id!, doc.id);
      setDocText(detail.extracted_text || 'No extracted text available.');
    } catch {
      setDocText('Failed to load document text.');
    } finally {
      setDocTextLoading(false);
    }
  };

  const handleRefreshData = async () => {
    if (!id || !caseData) return;
    const queryValue = caseData.case.ticker || caseData.case.issuer_name || '';
    if (!queryValue) {
      addToast('No issuer name or ticker to look up', 'error');
      return;
    }
    setRefreshingData(true);
    try {
      // Enqueue the refresh - POST returns 202 immediately with a run_id;
      // the multi-source live lookup runs in a background task so the UI
      // is not blocked. The pollingPdRunId useEffect polls until done.
      const resp = await triggerPublicLookup(id, caseData.case.ticker ? 'ticker' : 'name', queryValue);
      addToast('Public data refresh started', 'success');
      setPollingPdRunId(resp.run_id);
    } catch (err) {
      setRefreshingData(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      if (e?.response?.status === 409) {
        addToast('A refresh or review is already in progress', 'error');
      } else {
        addToast('Failed to refresh public data', 'error');
      }
    }
  };

  const handleRefreshSource = async (sourceName: string) => {
    if (!id || !caseData) return;
    const queryValue = caseData.case.ticker || caseData.case.issuer_name || '';
    if (!queryValue) {
      addToast('No issuer name or ticker to look up', 'error');
      return;
    }
    setRefreshingSource(sourceName);
    try {
      const resp = await triggerPublicLookup(id, caseData.case.ticker ? 'ticker' : 'name', queryValue, [sourceName]);
      addToast(sourceName + ' refresh started', 'success');
      setPollingPdRunId(resp.run_id);
    } catch (err) {
      setRefreshingSource(null);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      if (e?.response?.status === 409) {
        addToast('A refresh or review is already in progress', 'error');
      } else {
        addToast('Failed to refresh ' + sourceName, 'error');
      }
    }
  };


  const handleDetermination = async () => {
    if (!id || !detDecision) return;
    setDetSubmitting(true);
    try {
      const det = await recordDetermination(id, detDecision, detRationale);
      setDetermination(det);
      setShowDetConfirm(false);
      setDetDecision('');
      setDetRationale('');
      addToast('Determination recorded', 'success');
      await loadCase();
    } catch {
      addToast('Failed to record determination', 'error');
    } finally {
      setDetSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="skeleton h-8 w-48 mb-4" />
        <div className="skeleton h-10 w-full mb-6" />
        <div className="card p-8">
          <div className="skeleton h-6 w-1/3 mb-4" />
          <div className="skeleton h-4 w-full mb-2" />
          <div className="skeleton h-4 w-2/3 mb-2" />
          <div className="skeleton h-4 w-1/2" />
        </div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="max-w-7xl mx-auto text-center py-20">
        <AlertCircle size={48} className="mx-auto text-slate-300 mb-4" />
        <p className="text-slate-500">Case not found.</p>
      </div>
    );
  }

  const c = caseData.case;
  const flags = latestRun?.flags || [];
  const triggeredFlags = flags.filter(f => f.points > 0);
  const notVerifiableFlags = flags.filter(f => f.points === 0);
  const sortedFlags = [...triggeredFlags].sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9));
  const sortedNVFlags = [...notVerifiableFlags].sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9));
  const score = latestRun?.score;
  const summary = latestRun?.summary;
  const hasReview = reviewRuns.length > 0;

  return (
    <div className="max-w-7xl mx-auto">
      {/* Back link */}
      <Link to="/cases" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4">
        <ArrowLeft size={16} />
        Back to Cases
      </Link>

      {/* Case header */}
      <div className="card p-6 mb-6 animate-fade-in">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h1 className="text-2xl font-bold text-slate-900">{c.case_number}</h1>
              <StatusBadge status={c.status} />
              {c.tier && <TierBadge tier={c.tier} />}
              {c.needs_manual_review && (
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 text-amber-700 px-2.5 py-1 text-sm font-medium">
                  <AlertCircle size={14} />
                  Manual Review Needed
                </span>
              )}
              {c.modified_since_review && (
                <span className="inline-flex items-center gap-1 rounded-full border border-orange-300 bg-orange-50 text-orange-700 px-2.5 py-1 text-sm font-medium" title="Case facts were edited since the last review run. Re-pull public data + re-review to refresh the score.">
                  <AlertCircle size={14} />
                  Modified — needs re-review
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-500 flex-wrap">
              {c.issuer_name && <span>Issuer: <strong className="text-slate-700">{c.issuer_name}</strong></span>}
              {c.ticker && <span>Symbol: <strong className="text-slate-700">{c.ticker}</strong></span>}
              {c.depositor_name && <span>Depositor: <strong className="text-slate-700">{c.depositor_name}</strong></span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { if (id) downloadReviewRecordJson(id); }}
              className="btn-secondary inline-flex items-center gap-2 text-sm"
              title="Download the full review record as JSON"
            >
              <Download size={16} /> Review record (JSON)
            </button>
            <button
              onClick={() => { if (id) openReviewRecordHtml(id); }}
              className="btn-secondary inline-flex items-center gap-2 text-sm"
              title="Open a print-optimized review record for print / save as PDF"
            >
              <FileText size={16} /> Print / Save as PDF
            </button>
          </div>
          <div className="flex items-center gap-2">
            {reviewInProgress && (
              <button
                onClick={handleCancelReview}
                disabled={cancellingReview}
                className="btn-secondary inline-flex items-center gap-2 text-red-600 border-red-300 hover:bg-red-50"
                title="Cancel the in-progress review run"
              >
                {cancellingReview ? <Loader2 size={16} className="animate-spin" /> : <Ban size={16} />}
                Cancel Review
              </button>
            )}
            {hasReview ? (
              <button
                onClick={handleRunReview}
                disabled={reviewLoading || reviewInProgress}
                className="btn-secondary inline-flex items-center gap-2"
              >
                {reviewLoading || reviewInProgress ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                Re-run Review
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleReReview}
                  disabled={rereviewLoading || reviewInProgress}
                  className="btn-primary inline-flex items-center gap-2"
                  title="Pull public data (EDGAR, OTC, FINRA, OFAC, etc.) then run the rubric review"
                >
                  {rereviewLoading || reviewInProgress ? <Loader2 size={16} className="animate-spin" /> : <Database size={16} />}
                  Pull Public Data &amp; Run Review
                </button>
                <button
                  onClick={handleRunReview}
                  disabled={reviewLoading || reviewInProgress}
                  className="btn-secondary inline-flex items-center gap-2"
                  title="Run the rubric review without pulling new public data"
                >
                  {reviewLoading || reviewInProgress ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
                  Run Review
                </button>
              </div>
            )}
          </div>
        </div>
        {/* in-progress / failed-review banner */}
        {showRereviewBanner && !reviewInProgress && (
          <div className="mt-4 p-4 rounded-lg bg-orange-50 border border-orange-200 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <AlertCircle size={18} className="text-orange-600" />
              <span className="text-sm text-orange-800 font-medium">
                Case modified — re-pull public data + re-review?
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowRereviewBanner(false)}
                className="btn-secondary text-sm"
              >
                Not now
              </button>
              <button
                onClick={handleReReview}
                disabled={rereviewLoading}
                className="btn-primary inline-flex items-center gap-2 text-sm"
              >
                {rereviewLoading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                Update & Re-review
              </button>
            </div>
          </div>
        )}
        {reviewInProgress && (
          <div className="mt-4 p-3 rounded-lg bg-blue-50 border border-blue-200 flex items-center gap-2">
            <Loader2 size={16} className="animate-spin text-blue-600" />
            <span className="text-sm text-blue-800 font-medium">
              {firstReviewEver ? 'No results yet - first review in progress...' : 'Review in progress...'}
            </span>
          </div>
        )}
        {!reviewInProgress && reviewFailedMsg && (
          <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
            <AlertCircle size={16} className="text-red-600" />
            <span className="text-sm text-red-800 font-medium">Review failed: {reviewFailedMsg}</span>
          </div>
        )}
        {latestRun?.run && !reviewInProgress && latestRun.run.data_fetched_at && (
          <div className="mt-2 text-xs text-slate-400">
            Review based on public data fetched: {new Date(latestRun.run.data_fetched_at).toLocaleString()}
            {!publicData.length && ' (no public data available — use Refresh Data to fetch)'}
          </div>
        )}
        {latestRun?.run && !reviewInProgress && !latestRun.run.data_fetched_at && (
          <div className="mt-2 text-xs text-amber-500">
            No public data was used for this review. Click "Refresh Data" on the Public Data tab to fetch issuer information, then re-run the review.
          </div>
        )}
        {c.needs_manual_review && c.manual_review_fields?.length > 0 && (
          <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
            <p className="text-sm text-amber-800 font-medium mb-1">Missing fields requiring manual input:</p>
            <div className="flex gap-2 flex-wrap">
              {c.manual_review_fields.map((f) => (
                <span key={f} className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-300">{f}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mb-4">
        <div className="flex gap-1 border-b border-slate-200 overflow-x-auto" role="tablist">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <Icon size={16} />
                {tab.label}
                {tab.id === 'flags' && sortedFlags.length > 0 && (
                  <span className="ml-1 text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-600">{sortedFlags.length}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div className="card p-6 animate-fade-in">
        {/* OVERVIEW */}
        {activeTab === 'overview' && (
          <div>
            <WorkflowPanel caseId={c.id} caseStatus={c.status} onStatusChange={() => {}} />
            <div className="flex items-center justify-between mb-4 mt-6">
              <h2 className="text-lg font-semibold text-slate-800">Case Facts</h2>
              {editing ? (
                <div className="flex gap-2">
                  <button onClick={() => { setEditing(false); resetEditFromCase(c); }} className="btn-secondary text-sm">Cancel</button>
                  <button onClick={handleSaveCase} className="btn-primary text-sm">Save</button>
                </div>
              ) : (
                <button onClick={() => setEditing(true)} className="btn-secondary text-sm">Edit</button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {editing ? (
                <label className="flex flex-col gap-1">
                  <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Issuer Name</span>
                  <input value={editFacts.issuer_name} onChange={(e) => setEditFacts({ ...editFacts, issuer_name: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
                </label>
              ) : <FactField label="Issuer Name" value={c.issuer_name} />}
              {editing ? (
                <label className="flex flex-col gap-1">
                  <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Ticker Symbol</span>
                  <input value={editFacts.issuer_symbol} onChange={(e) => setEditFacts({ ...editFacts, issuer_symbol: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
                </label>
              ) : <FactField label="Ticker Symbol" value={c.ticker} />}
              {editing ? (
                <label className="flex flex-col gap-1">
                  <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">CUSIP</span>
                  <input value={editFacts.cusip} onChange={(e) => setEditFacts({ ...editFacts, cusip: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
                </label>
              ) : <FactField label="CUSIP" value={c.cusip} />}
              {editing ? (
                <label className="flex flex-col gap-1">
                  <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Depositor Name</span>
                  <input value={editFacts.depositor_name} onChange={(e) => setEditFacts({ ...editFacts, depositor_name: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
                </label>
              ) : <FactField label="Depositor Name" value={c.depositor_name} />}
              {editing ? (
                <label className="flex flex-col gap-1">
                  <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Depositor Type</span>
                  <input value={editFacts.depositor_type} onChange={(e) => setEditFacts({ ...editFacts, depositor_type: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
                </label>
              ) : <FactField label="Depositor Type" value={c.depositor_type} />}
              {editing ? (
                <label className="flex flex-col gap-1">
                  <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Account Reference</span>
                  <input value={editFacts.account_ref} onChange={(e) => setEditFacts({ ...editFacts, account_ref: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
                </label>
              ) : <FactField label="Account Reference" value={c.account_ref} />}
              {editing ? (
                <label className="flex flex-col gap-1">
                  <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Deposit Date</span>
                  <input type="date" value={editFacts.deposit_date} onChange={(e) => setEditFacts({ ...editFacts, deposit_date: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
                </label>
              ) : <FactField label="Deposit Date" value={c.deposit_date} />}
              {editing ? (
                <label className="flex flex-col gap-1">
                  <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Share Quantity</span>
                  <input type="number" min="0" step="1" value={editFacts.share_quantity} onChange={(e) => setEditFacts({ ...editFacts, share_quantity: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
                </label>
              ) : <FactField label="Share Quantity" value={c.share_quantity?.toLocaleString()} />}
            </div>
            <div className="mt-6">
              <label className="block text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Notes</label>
              {editing ? (
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={4}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                />
              ) : (
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{c.notes || 'No notes.'}</p>
              )}
            </div>
            {caseData.facts.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Extracted Facts ({caseData.facts.length})</h3>
                <div className="space-y-2">
                  {caseData.facts.map((f: CaseFact, i: number) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-700">{f.field_name}</p>
                        <p className="text-sm text-slate-600">{f.field_value || '--'}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-200 text-slate-600">{f.source_type}</span>
                        {f.is_verifiable ? (
                          <CheckCircle2 size={14} className="text-green-500" />
                        ) : (
                          <AlertCircle size={14} className="text-amber-500" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* DOCUMENTS */}
        {activeTab === 'documents' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800">Documents ({documents.length + uploadingFiles.length})</h2>
              <div className="flex items-center gap-3">
                {isComplianceOrOperator && documents.some((d) => !d.is_purged) && (
                  <button
                    onClick={handlePurgeAll}
                    className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700 font-medium"
                    title="Delete ALL original files for this case from disk (metadata kept)"
                  >
                    <Trash2 size={16} />
                    Purge all originals
                  </button>
                )}
                <label className={uploadingDocs ? 'block opacity-50 pointer-events-none' : 'block'}>
                <input
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => handleUploadDocuments(e.target.files)}
                />
                <span className="btn-secondary inline-flex items-center gap-2 cursor-pointer text-sm">
                  {uploadingDocs ? (
                    <><Loader2 className="animate-spin" size={16} /></>
                  ) : (
                    <><Upload size={16} /></>
                  )}
                  {uploadingDocs ? 'Uploading...' : 'Upload'}
                </span>
              </label>
              </div>
            </div>
            <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>
                Uploading additional documents runs extraction, AI analysis, and PII stripping —
                but it does <strong>not</strong> automatically update case fields
                (issuer name, ticker, CUSIP, deposit date, share quantity, etc.).
                If the new document has better values, edit them manually on the Intake tab.
              </span>
            </div>
            {uploadingDocs && uploadProgress && (
              <div className="card p-4 mb-4 animate-fade-in">
                <div className="flex items-center gap-3">
                  <Loader2 className="animate-spin text-primary-600 shrink-0" size={20} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700">
                      Uploading document {uploadProgress.current + 1} of {uploadProgress.total}
                    </p>
                    <p className="text-xs text-slate-400 truncate">{uploadProgress.name}</p>
                  </div>
                </div>
                <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-600 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress.total > 0 ? ((uploadProgress.current + 1) / uploadProgress.total) * 100 : 0}%` }}
                  />
                </div>
              </div>
            )}
            {documents.length === 0 && uploadingFiles.length === 0 ? (
              <>
              <div className="mb-4">
                <label className={uploadingDocs ? 'block opacity-50 pointer-events-none' : 'block'}>
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => handleUploadDocuments(e.target.files)}
                  />
                  <span className="btn-secondary inline-flex items-center gap-2 cursor-pointer">
                    {uploadingDocs ? (
                      <Loader2 className="animate-spin" size={16} />
                    ) : (
                      <Upload size={16} />
                    )}
                    {uploadingDocs ? 'Uploading...' : 'Upload Documents'}
                  </span>
                </label>
              </div>
              <EmptyState icon={Upload} title="No documents" message="Upload documents to this case to begin analysis." />
            </>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 text-left">
                      <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">File</th>
                      <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Type</th>
                      <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Size</th>
                      <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                      <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadingFiles.map((uf, idx) => (
                      <tr key={`uploading-${idx}`} className="bg-slate-50 opacity-60">
                        <td className="px-4 py-3 text-sm text-slate-500">
                          <div className="flex items-center gap-2">
                            {uf.done ? (
                              <CheckCircle size={14} className="text-green-500 shrink-0" />
                            ) : (
                              <Loader2 size={14} className="animate-spin text-primary-500 shrink-0" />
                            )}
                            <span className="truncate">{uf.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-400">—</td>
                        <td className="px-4 py-3 text-sm text-slate-400">{(uf.size / 1024).toFixed(0)} KB</td>
                        <td className="px-4 py-3 text-sm">
                          <span className="inline-flex items-center gap-1 text-slate-400">
                            {uf.done ? 'Done' : 'Uploading...'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-300">—</td>
                      </tr>
                    ))}
                    {documents.map((doc) => (
                      <DocumentRow key={doc.id} doc={doc} caseId={id!} onView={handleViewDoc} onPurged={handlePurged} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/* per-case master-doc override + intake discrepancies */}
            {documents.length > 0 && (
              <div className="mt-6">
                <RulesOverridePanel caseId={id!} documents={documents} />
              </div>
            )}
          </div>
        )}

        {/* PUBLIC DATA */}
        {activeTab === 'public-data' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800">Public Data</h2>
              <div className="flex gap-2">
                <button onClick={() => window.print()} className="btn-secondary inline-flex items-center gap-2 text-sm">
                  <Printer size={16} /> Print / Save PDF
                </button>
                <button onClick={handleRefreshData} disabled={refreshingData || reviewInProgress} className="btn-secondary inline-flex items-center gap-2 text-sm">
                {refreshingData || reviewInProgress ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                Refresh Data
              </button>
              </div>
            </div>
            {publicData.length === 0 ? (
              <EmptyState icon={Database} title="No public data" message="Run a public data lookup to fetch issuer information from EDGAR, OTC Markets, and other sources." />
            ) : (
              <div className="space-y-3">
                {groupedPublicData.map((grp) => {
                  return (
                  <div key={grp.source} className="p-4 rounded-lg border border-slate-200 bg-slate-50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-700 uppercase">{grp.source}</span>
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-200 text-slate-600">{grp.items.filter(pd => pd.query_type !== "screenshot_path" && pd.query_type !== "pdf_path").length} data points</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">{new Date(grp.retrieved_at).toLocaleString()}</span>
                        <button
                          onClick={() => handleRefreshSource(grp.source)}
                          disabled={refreshingSource === grp.source || refreshingData}
                          className="text-xs text-primary-600 hover:text-primary-700 inline-flex items-center gap-1"
                          aria-label={'Refresh ' + grp.source}
                        >
                          {refreshingSource === grp.source ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                          Refresh
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mb-2">
                      {grp.source_url && (
                        <a href={grp.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 underline">
                          <ExternalLink size={12} />
                          View Source Page
                        </a>
                      )}
                      <button
                        onClick={() => setViewingStoredData(viewingStoredData === grp.source ? null : grp.source)}
                        className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 underline"
                      >
                        <Database size={12} />
                        {viewingStoredData === grp.source ? 'Hide Stored Data' : 'View Stored Data'}
                      </button>
                    </div>
                    {viewingStoredData === grp.source && (
                      <div className="mb-2 bg-slate-900 rounded-lg p-3 overflow-x-auto max-h-96 overflow-y-auto">
                        {grp.items.filter(pd => pd.query_type !== 'screenshot_path' && pd.query_type !== 'pdf_path').map((pd) => (
                          <div key={pd.id} className="mb-2">
                            <div className="text-xs text-slate-400 mb-1">{pd.query_type}</div>
                            <pre className="text-xs text-green-300 whitespace-pre-wrap break-words">
{JSON.stringify(pd.result_data, null, 2)}
                            </pre>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="bg-white rounded-lg p-3 border border-slate-100">
                      {grp.items.filter(pd => pd.query_type !== 'screenshot_path' && pd.query_type !== 'pdf_path').map((pd) => {
                        const pdItems = formatPublicData(pd.result_data);
                        const pdKey = pd.query_type || pdItems[0]?.key || '';
                        return pdItems.map((item, i) => (
                          <div key={pd.id + '-' + i} className="flex justify-between gap-4 py-1 border-b border-slate-50 last:border-0">
                            <span className="text-xs font-medium text-slate-500 shrink-0 whitespace-nowrap">{prettyKey(item.key === 'value' ? pdKey : (item.key || pdKey))}</span>
                            <div className="text-right">
                              <span className="text-xs text-slate-700">{renderValue(item.value, item.key === 'value' ? pdKey : (item.key || pdKey))}</span>
                            </div>
                          </div>
                        ));
                      })}
                      {grp.items.length === 0 && (
                        <p className="text-xs text-slate-400">No data points returned.</p>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}

            {/* Search History */}
            {searchHistory.length > 0 && (
              <div className="mt-6">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="text-sm font-medium text-slate-500 hover:text-slate-700 inline-flex items-center gap-2"
                >
                  <History size={16} />
                  Search History ({searchHistory.length} {searchHistory.length === 1 ? 'run' : 'runs'})
                  {showHistory ? ' - hide' : ' - show'}
                </button>
                {showHistory && (
                  <div className="mt-3 space-y-3">
                    {searchHistory.map((run, i) => {
                      // Group items by source within this fetch batch
                      const bySrc: Record<string, PublicDataResult[]> = {};
                      for (const item of (run.items || [])) {
                        if (!bySrc[item.source]) bySrc[item.source] = [];
                        bySrc[item.source].push(item);
                      }
                      const sources = Object.keys(bySrc).sort();
                      return (
                        <div key={i} className="p-3 rounded-lg border border-slate-200 bg-white">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-slate-600">
                                {new Date(run.timestamp).toLocaleString()}
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-500">
                                {run.result_count} results from {run.source_count} sources
                              </span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            {sources.map(src => {
                              const srcItems = bySrc[src].filter(pd => pd.query_type !== 'screenshot_path' && pd.query_type !== 'pdf_path');
                              return (
                                <div key={src} className="p-2 rounded border border-slate-100 bg-slate-50">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-semibold text-slate-600 uppercase">{src}</span>
                                    <span className="text-xs text-slate-400">{srcItems.length} data points</span>
                                  </div>
                                  <div className="bg-white rounded p-2 border border-slate-100">
                                    {srcItems.map((pd) => {
                                      const pdItems = formatPublicData(pd.result_data);
                                      const pdKey = pd.query_type || pdItems[0]?.key || '';
                                      return pdItems.map((item, j) => (
                                        <div key={pd.id + '-' + j} className="flex justify-between gap-4 py-0.5 border-b border-slate-50 last:border-0">
                                          <span className="text-xs font-medium text-slate-500 shrink-0 whitespace-nowrap">{prettyKey(item.key === 'value' ? pdKey : (item.key || pdKey))}</span>
                                          <div className="text-right">
                                            <span className="text-xs text-slate-700">{renderValue(item.value, item.key === 'value' ? pdKey : (item.key || pdKey))}</span>
                                          </div>
                                        </div>
                                      ));
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* RED FLAGS */}
        {activeTab === 'flags' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800">Triggered Red Flags ({sortedFlags.length})</h2>
              <div className="flex items-center gap-2">
                {user?.can_train_rules && (
                  <button
                    onClick={() => setShowTrainModal(true)}
                    className="btn-primary text-sm inline-flex items-center gap-2"
                    title="Generate a learned rule from a deficiency seen on this case"
                  >
                    <Sparkles size={16} /> Train from this case
                  </button>
                )}
                <button onClick={() => window.print()} className="btn-secondary text-sm inline-flex items-center gap-2">
                  <Printer size={16} /> Print
                </button>
              </div>
            </div>
            {score && (
              <div className="flex items-center gap-4 mb-6 p-4 rounded-lg bg-slate-50 border border-slate-200">
                <div className="text-center">
                  <p className="text-3xl font-bold text-slate-800">{score.aggregate_score}</p>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Score</p>
                </div>
                <div className="h-12 w-px bg-slate-200" />
                <div>
                  <TierBadge tier={score.tier} size="lg" />
                  {score.has_auto_escalate && (
                    <p className="text-xs text-red-600 mt-1">Auto-escalate flag triggered</p>
                  )}
                </div>
              </div>
            )}
            {sortedFlags.length === 0 ? (
              <EmptyState icon={Flag} title="No flags triggered" message="This case has no triggered red flags." />
            ) : (
              <div className="space-y-3">
                {sortedFlags.map((flag) => (
                  <FlagCard
                    key={flag.id}
                    flag={flag}
                    onOverride={handleOverrideFlag}
                    canFeedback={canFeedback}
                    feedback={flagFeedback[flag.id] ?? null}
                    onFeedback={handleFlagFeedback}
                  />
                ))}
              </div>
            )}
            {sortedNVFlags.length > 0 && (
              <div className="mt-6">
                <details className="rounded-lg border border-slate-200">
                  <summary className="cursor-pointer p-3 text-sm font-medium text-slate-500 hover:text-slate-700">
                    Not Verifiable ({sortedNVFlags.length}) - click to expand
                  </summary>
                  <div className="px-3 pb-3 space-y-1">
                    {sortedNVFlags.map((flag) => (
                      <div key={flag.id} className="flex items-center gap-2 text-xs text-slate-400">
                        <AlertCircle size={12} className="shrink-0 text-amber-400" />
                        <span>
                          {flag.flag_code}: {flag.fact_summary || 'Not verifiable'}
                          {flag.source_citation && (
                            <span className="text-slate-500"> — Source: {flag.source_citation}</span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            )}

            {/* Missed abnormality composer (assigned personnel only). */}
            {canFeedback && (
              <div className="mt-6 p-4 rounded-lg border border-indigo-100 bg-indigo-50/30">
                <h3 className="text-sm font-semibold text-indigo-800 uppercase tracking-wide mb-2">
                  Missed abnormality
                </h3>
                <p className="text-xs text-slate-500 mb-2">
                  Record something you caught that the AI did NOT flag. These notes feed the learning cycle.
                </p>
                <textarea
                  className="w-full text-sm rounded border border-indigo-200 p-2 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                  rows={3}
                  placeholder="e.g. The legal opinion cites an unrelated issuer name that the AI did not flag as a mismatch."
                  value={missedNote}
                  onChange={(e) => setMissedNote(e.target.value)}
                />
                <div className="mt-2">
                  <button
                    className="btn-primary text-sm inline-flex items-center gap-1"
                    disabled={missedSubmitting || !missedNote.trim()}
                    onClick={handleAddMissedAbnormality}
                  >
                    {missedSubmitting ? 'Saving…' : 'Add missed abnormality'}
                  </button>
                </div>
                {missedAbnormalities.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {missedAbnormalities.map((ma) => (
                      <div key={ma.id} className="p-3 rounded border border-slate-200 bg-white">
                        <p className="text-sm text-slate-700">{ma.description}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(ma.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* SUMMARY */}
        {activeTab === 'summary' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800">AI Summary</h2>
              <button onClick={() => window.print()} className="btn-secondary text-sm inline-flex items-center gap-2">
                <Printer size={16} /> Print / Save PDF
              </button>
            </div>
            {!summary ? (
              <EmptyState icon={ScrollText} title="No summary available" message="Run a review to generate the AI summary." />
            ) : (
              <div className="space-y-6">
                {score && (
                  <div className="flex items-center gap-4 p-4 rounded-lg bg-slate-50 border border-slate-200">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-slate-800">{score.aggregate_score}</p>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Aggregate Score</p>
                    </div>
                    <div className="h-12 w-px bg-slate-200" />
                    <TierBadge tier={score.tier} size="lg" />
                  </div>
                )}
                {summary.summary_text && (
                  <div className="card p-6">
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Summary</h3>
                    <div dangerouslySetInnerHTML={{ __html: renderMarkdown(summary.summary_text) }} />
                  </div>
                )}
                {summary.not_verifiable_items?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Not Verifiable</h3>
                    <ul className="space-y-1">
                      {summary.not_verifiable_items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                          <AlertCircle size={14} className="shrink-0 mt-0.5 text-amber-500" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {summary.worksheet_fields && Object.keys(summary.worksheet_fields).length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Worksheet Fields</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {Object.entries(summary.worksheet_fields).map(([k, v]) => (
                        <div key={k} className="flex justify-between p-2 rounded bg-slate-50 border border-slate-100">
                          <span className="text-sm text-slate-500">{k}</span>
                          <span className="text-sm font-medium text-slate-700">{String(v)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* DETERMINATION */}
        {activeTab === 'determination' && (
          <div>
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Determination</h2>
            {determination ? (
              <div className="space-y-4">
                <div className="p-4 rounded-lg border border-slate-200 bg-slate-50">
                  <div className="flex items-center gap-3 mb-3">
                    <DetBadge decision={determination.decision} />
                    <span className="text-sm text-slate-500">{new Date(determination.determined_at).toLocaleString()}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-1">Rationale</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{determination.rationale}</p>
                  </div>
                </div>
                {isPrincipal && (
                  <p className="text-sm text-slate-500">A determination has already been recorded for this case.</p>
                )}
              </div>
            ) : isPrincipal ? (
              <div>
                <p className="text-sm text-slate-500 mb-4">Record a determination for this case. This action is audited.</p>
                <div className="space-y-3 mb-4">
                  <label className="block text-sm font-semibold text-slate-500 uppercase tracking-wide">Decision</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {(['APPROVE', 'ESCALATE', 'REJECT'] as const).map((dec) => (
                      <button
                        key={dec}
                        onClick={() => setDetDecision(dec)}
                        className={`p-4 rounded-lg border-2 text-center transition-all ${
                          detDecision === dec
                            ? DECISION_STYLES[dec].active
                            : 'border-slate-200 hover:border-slate-300 bg-white'
                        }`}
                      >
                        <span className={`text-sm font-medium ${detDecision === dec ? DECISION_STYLES[dec].text : 'text-slate-600'}`}>
                          {dec.charAt(0) + dec.slice(1).toLowerCase()}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Rationale</label>
                  <textarea
                    value={detRationale}
                    onChange={(e) => setDetRationale(e.target.value)}
                    rows={4}
                    placeholder="Enter the rationale for this determination..."
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <button
                  onClick={() => setShowDetConfirm(true)}
                  disabled={!detDecision || !detRationale.trim()}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <Gavel size={16} />
                  Record Determination
                </button>
                {showDetConfirm && (
                  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" role="dialog" aria-modal="true">
                    <div className="card p-6 max-w-md w-full mx-4 animate-fade-in">
                      <h3 className="text-lg font-semibold text-slate-800 mb-2">Confirm Determination</h3>
                      <p className="text-sm text-slate-600 mb-4">
                        You are about to record a decision of <strong>{detDecision}</strong>. This action is permanent and audited.
                      </p>
                      <div className="flex gap-3 justify-end">
                        <button onClick={() => setShowDetConfirm(false)} className="btn-secondary">Cancel</button>
                        <button onClick={handleDetermination} disabled={detSubmitting} className="btn-primary inline-flex items-center gap-2">
                          {detSubmitting && <Loader2 size={16} className="animate-spin" />}
                          Confirm
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <EmptyState icon={Gavel} title="No determination yet" message="Only a principal can record a determination." />
            )}
          </div>
        )}

        {/* AUDIT TRAIL */}
        {activeTab === 'audit' && (
          <div>
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Audit Trail</h2>
            {auditEntries.length === 0 ? (
              <EmptyState icon={History} title="No audit entries" message="Actions on this case will appear here." />
            ) : (
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-px bg-slate-200" />
                <div className="space-y-4">
                  {auditEntries.map((entry) => (
                    <div key={entry.id} className="relative pl-12">
                      <div className="absolute left-2.5 top-1 w-3 h-3 rounded-full bg-primary-500 border-2 border-white" />
                      <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-slate-700">{entry.action}</span>
                          <span className="text-xs px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">{entry.entity_type}</span>
                        </div>
                        <p className="text-xs text-slate-400">{new Date(entry.timestamp).toLocaleString()}</p>
                        {entry.user_id && <p className="text-xs text-slate-500 mt-1">User: {entry.user_id}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* CHAT */}
        {activeTab === 'chat' && (
          <div>
            <h2 className="text-lg font-semibold text-slate-800 mb-1">Chat</h2>
            <p className="text-xs text-slate-500 mb-4">
              Ask questions against this case's documents and public data.
              Answers are sourced from case context with citations and are
              recorded in the audit trail.
            </p>
            <ChatPanel
              caseId={id!}
              issuerName={caseData.case.issuer_name}
              ticker={caseData.case.ticker}
              caseNumber={caseData.case.case_number}
              onSavedResponsesChange={() => setSavedResponsesNonce((n) => n + 1)}
            />
            {/* saved responses view */}
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
                Saved responses
              </h3>
              <SavedResponsesPanel
                caseId={id!}
                refreshNonce={savedResponsesNonce}
              />
            </div>
          </div>
        )}
      </div>

      {/* Document viewer modal */}
      {viewingDoc && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" role="dialog" aria-modal="true" onClick={() => setViewingDoc(null)}>
          <div className="card max-w-3xl w-full mx-4 max-h-[80vh] flex flex-col animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">{viewingDoc.original_filename}</h3>
                <p className="text-xs text-slate-400 font-mono">{viewingDoc.id}</p>
              </div>
              <button onClick={() => setViewingDoc(null)} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {docTextLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={24} className="animate-spin text-slate-400" />
                </div>
              ) : (
                <pre className="text-sm text-slate-700 whitespace-pre-wrap font-mono">{docText}</pre>
              )}
            </div>
          </div>
        </div>
      )}

      {/* "Train from this case" modal -- case is locked. */}
      {showTrainModal && caseData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Sparkles className="text-primary-600" size={18} />
                <h3 className="text-sm font-semibold text-slate-800">Train a learned rule from this case</h3>
              </div>
              <button onClick={() => setShowTrainModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-4">
              <CreateRulePanel
                cases={[caseData.case]}
                casesLoading={false}
                lockedCaseId={caseData.case.id}
                onCreated={() => {
                  setShowTrainModal(false);
                  addToast('Learned rule created -- publish in Admin, Learned Rules', 'success');
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FactField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-sm text-slate-700">{value || '--'}</p>
    </div>
  );
}

function EmptyState({ icon: Icon, title, message }: { icon: typeof FileText; title: string; message: string }) {
  return (
    <div className="flex flex-col items-center text-center py-12">
      <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-3">
        <Icon className="text-slate-400" size={28} />
      </div>
      <h3 className="text-sm font-semibold text-slate-700 mb-1">{title}</h3>
      <p className="text-sm text-slate-500 max-w-sm">{message}</p>
    </div>
  );
}

function DetBadge({ decision }: { decision: Determination['decision'] }) {
  const styles: Record<string, string> = {
    APPROVE: 'bg-green-100 text-green-700 border-green-300',
    ESCALATE: 'bg-amber-100 text-amber-700 border-amber-300',
    REJECT: 'bg-red-100 text-red-700 border-red-300',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium ${styles[decision]}`}>
      {decision.charAt(0) + decision.slice(1).toLowerCase()}
    </span>
  );
}

const DECISION_STYLES: Record<string, { active: string; text: string }> = {
  APPROVE: { active: 'border-green-500 bg-green-50', text: 'text-green-700' },
  ESCALATE: { active: 'border-amber-500 bg-amber-50', text: 'text-amber-700' },
  REJECT: { active: 'border-red-500 bg-red-50', text: 'text-red-700' },
};